import { TrackingState, SerialiseType, RelationType, TimeZoneFormat, DataType } from '../Model/Misc';
import { Deserialiser } from './Deserialiser';
import ValueObject from '../Model/ValueObject';
import DateUtils from '../Dates/DateUtils';
import NumberUtils from "../NumberUtils";
var referenceIdKey = Symbol("referenceId");
var JSFormatter = /** @class */ (function () {
    function JSFormatter() {
    }
    JSFormatter.serialise = function (object, options) {
        if (options === void 0) { options = {}; }
        var s = new Serialiser();
        return s.serialise(object, options);
    };
    /**
     * Deserialises a plain javascript object into a NeoModel instance
     * @param from plain javascript object
     * @param to object or array
     * @param rootType constructor for the root type. E.g. if deserialising into an array of Animal, pass in Animal.
     * @param options Deserialise options
     * @param justCreated True if the object being deserialised was just created (for its onDeserialise method).
     */
    JSFormatter.deserialise = function (from, to, rootType, options, justCreated) {
        if (options === void 0) { options = {}; }
        if (justCreated === void 0) { justCreated = false; }
        if (!from) {
            throw "Cannot deserialise from null value.";
        }
        var d = new Deserialiser();
        d.deserialise(from, to, rootType, options, justCreated);
    };
    return JSFormatter;
}());
export { JSFormatter };
var Serialiser = /** @class */ (function () {
    function Serialiser() {
        this.objectRefs = {};
        this.options = {};
        this.currentID = 1;
    }
    Serialiser.prototype.serialise = function (fromObject, options) {
        if (options === void 0) { options = {}; }
        this.options = options;
        var to;
        this.currentID = 1;
        if (fromObject instanceof Array) {
            to = [];
            this.serialiseArray(fromObject, to);
        }
        else {
            to = {};
            this.serialiseObject(fromObject, to);
        }
        this.cleanUpObjects();
        return to;
    };
    /**
     * removes the temporary ids from the from objects.
     */
    Serialiser.prototype.cleanUpObjects = function () {
        for (var id in this.objectRefs) {
            var refInfo = this.objectRefs[id];
            delete refInfo.from[referenceIdKey];
            // Remove $id property if object is not referenced by anything.
            if (!refInfo.isReferenced) {
                delete refInfo.to.$id;
            }
        }
        this.objectRefs = {};
    };
    Serialiser.prototype.serialiseObject = function (from, to, isDictionary) {
        if (isDictionary === void 0) { isDictionary = false; }
        //check that this object hasnt already been serialised.
        var referenceId = from[referenceIdKey];
        if (!referenceId) {
            referenceId = this.currentID++;
            from[referenceIdKey] = referenceId;
        }
        var referenceInfo = this.objectRefs[referenceId];
        if (referenceInfo) {
            //already serialised this object
            referenceInfo.isReferenced = true;
            to.$ref = referenceInfo.to.$id;
            return;
        }
        else {
            this.objectRefs[referenceId] = { from: from, to: to };
            to.$id = referenceId.toString();
        }
        //Set tracking state
        if (!this.options.suppressTrackingState && !isDictionary) {
            if (from.isNew) {
                to.trackingState = TrackingState.Added;
            }
            else if (from.isSelfDirty) {
                to.trackingState = TrackingState.Modified;
            }
            else {
                to.trackingState = TrackingState.Unchanged;
            }
            to.entityIdentifier = from.entityIdentifier;
        }
        //Loop through serialisable properties
        var typeInfo = from.constructor.neo;
        if (!typeInfo && !isDictionary) {
            throw "Object is not decorated with @NeoModel. Either add @NeoModel, suppress serialisation on the property, or add @Attributes.Raw() to the property.";
        }
        var propertyMap = isDictionary ? {} : typeInfo.propertyMap;
        for (var key in from) {
            if (from.hasOwnProperty(key) && key.indexOf("__") !== 0) {
                try {
                    var propertyInfo = isDictionary ? {} : propertyMap[key];
                    if (propertyInfo &&
                        !propertyInfo.isComputed && !propertyInfo.isReadOnly && // Safari seems to enumerate properties like isDirty, other browsers wont include these properties.
                        propertyInfo.serialise !== SerialiseType.Never &&
                        propertyInfo.serialise !== SerialiseType.MappingOnly &&
                        (propertyInfo.serialise !== SerialiseType.FullOnly || this.options.includeClean)) {
                        //@ts-ignore
                        var fromValue = from[key];
                        var isChildObject = (fromValue && fromValue instanceof Object && !(fromValue instanceof Date) && !(NumberUtils.decimalService.isDecimal(fromValue))) || propertyInfo.relationType === RelationType.ChildObject;
                        if (fromValue instanceof Array) {
                            // Arrays of simple types are always included.
                            if (!this.options.suppressChildren || (fromValue.length > 0 && !(fromValue[0] instanceof Object))) {
                                to[key] = [];
                                this.serialiseArray(fromValue, to[key]);
                            }
                        }
                        else if (isChildObject && !propertyInfo.assignOnDeserialise) {
                            if (!fromValue || (propertyInfo.relationType === RelationType.Parent && !fromValue[referenceIdKey])) {
                                // Parents must only be included if they reference an already serialised object.
                                // Otherwise serialising a leaf node will serialise up the tree and the entire graph will be serialised.
                            }
                            else {
                                var shouldSerialise = !this.options.suppressChildren || fromValue instanceof ValueObject || propertyInfo.relationType === RelationType.Dictionary;
                                if (fromValue.canSerialise !== undefined) {
                                    shouldSerialise = fromValue.canSerialise(shouldSerialise);
                                }
                                if (shouldSerialise) {
                                    to[key] = {};
                                    this.serialiseObject(fromValue, to[key], propertyInfo.relationType === RelationType.Dictionary);
                                }
                            }
                        }
                        else {
                            // Simple values and dates
                            if (fromValue !== null || propertyInfo.serialise !== SerialiseType.NotNull) {
                                if (fromValue !== null && propertyInfo.propertyType === DataType.Decimal) {
                                    to[key] = NumberUtils.decimalService.toNumber(fromValue);
                                }
                                else if (propertyInfo.timeZoneSerialisation === TimeZoneFormat.None && fromValue instanceof Date) {
                                    to[key] = DateUtils.format(fromValue, "yyyy-MM-dd") + "T00:00:00";
                                }
                                else {
                                    to[key] = fromValue;
                                }
                            }
                        }
                    }
                }
                catch (error) {
                    throw "Error serialising property '".concat(key, "' on type '").concat(from.constructor.name, "': ").concat(error);
                }
            }
        }
    };
    Serialiser.prototype.serialiseArray = function (fromArray, toArray) {
        for (var i = 0; i < fromArray.length; i++) {
            var fromItem = fromArray[i];
            if (fromItem instanceof Object && !(fromItem instanceof Date)) {
                //complex object
                var shouldSerialise = this.options.includeClean || !!fromItem.isDirty;
                if (fromItem.canSerialise !== undefined) {
                    shouldSerialise = fromItem.canSerialise(shouldSerialise);
                }
                if (shouldSerialise) {
                    var toItem = {};
                    this.serialiseObject(fromItem, toItem);
                    toArray.push(toItem);
                }
            }
            else {
                //simple value
                toArray.push(fromItem);
            }
        }
        // add the deleted items.
        var list = fromArray;
        if (list.deletedList) {
            for (var i = 0; i < list.deletedList.length; i++) {
                var fromItem = list.deletedList[i];
                if (!fromItem.isNew) {
                    var toItem = {};
                    this.serialiseObject(fromItem, toItem);
                    toItem.trackingState = TrackingState.Deleted;
                    toArray.push(toItem);
                }
            }
        }
    };
    return Serialiser;
}());
export { Serialiser };
